/* eslint-disable no-console */
import { useEffect, useState } from 'react';
import { getPlans } from '../../plans/utils';
import { ResolvedPlan } from '../../../types/plans';
import { FetchContextProps } from '../../../context/FetchProvider';
import { PlanDealCode } from '../../../types/composer';
import { resolvePlans } from '../../../lib/composer/resolvePlans';

type UsePlansParams = {
  program?: string;
  productID?: string;
  planLengths?: string[];
  planVersion?: string;
  program2?: string;
  productID2?: string;
  planLengths2?: string[];
  planVersion2?: string;
  dealType?: string;
  dealCode?: string;
  dealCodes?: PlanDealCode[];
  fetch: Partial<FetchContextProps>; // Use Partial to make certain methods optional
};

/**
 * Custom hook to fetch and group plans by code for the PlanSelector component.
 * Returns.
 */
export function usePlans({
  program,
  productID,
  planLengths,
  planVersion,
  program2,
  productID2,
  planLengths2,
  planVersion2,
  dealType,
  dealCode,
  dealCodes,
  fetch,
}: UsePlansParams) {
  const [plans, setPlans] = useState<ResolvedPlan[]>([]);
  const [plansByCode, setPlansByCode] = useState<{
    [productID: string]: ResolvedPlan[];
  }>({});
  const [keys, setKeys] = useState<string[]>([]);
  const [suitePlans, setSuitePlans] = useState<ResolvedPlan[] | null>(null);

  // Callers often pass these as inline literals, so the fetch effect below
  // depends on their contents rather than their identity - otherwise every
  // render starts another round of requests.
  const planLengthsKey = JSON.stringify(planLengths || []);
  const planLengths2Key = JSON.stringify(planLengths2 || []);
  const dealCodesKey = JSON.stringify(dealCodes || []);

  // buttons show if there is a program2 &
  // that program 1 & 2 include either basic science or suite

  useEffect(() => {
    type FetchPlansArgs = {
      program: string;
      productID: string;
      planLengths: string[];
      planVersion?: string;
    };

    const fetchPlansSet = ({
      program,
      productID,
      planLengths,
      planVersion,
    }: FetchPlansArgs) => {
      if (!program) return [];
      return resolvePlans({
        planLengths,
        dealCodes,
        defaultDealCode: dealCode,
        fetchPlans: async ({ planLengths, dealCode }) => {
          try {
            return await getPlans({
              program,
              productID,
              planLengths,
              fetch: fetch as FetchContextProps,
              planVersion,
              dealType,
              dealCode,
            });
          } catch (error) {
            console.error('Error fetching plans:', error);
            return [];
          }
        },
      });
    };

    // Only fetch plans if valid program is provided and explicitly selected
    // Note: We allow empty productID for the "Program Default" option

    const shouldFetchFirstSet = program && program !== '';
    const shouldFetchSecondSet =
      program2 && program2 !== '' && productID2 && productID2 !== '';

    // If neither set has valid data, don't fetch anything
    if (!shouldFetchFirstSet && !shouldFetchSecondSet) {
      setPlansByCode({});
      setPlans([]);
      setKeys([]);
      return;
    }

    // Create array of fetch promises for sets that have valid data
    const fetchPromises = [];

    // Programs that are valid program codes, not product IDs
    const programCodes = [
      'MD',
      'DO',
      'PA',
      'NP',
      'RN',
      'DN',
      'PH',
      'OH',
      'LPN',
    ];

    if (shouldFetchFirstSet) {
      fetchPromises.push(
        fetchPlansSet({
          program: program || '',
          // Only use productID if it's not a program value or an empty string
          productID:
            productID &&
            typeof productID === 'string' &&
            productID !== '' &&
            !programCodes.includes(productID)
              ? productID
              : '',
          planLengths: planLengths || [],
          planVersion,
        }),
      );
    } else {
      fetchPromises.push([]); // Empty placeholder for first set
    }

    if (shouldFetchSecondSet) {
      fetchPromises.push(
        fetchPlansSet({
          program: program2 || '',
          // Only use productID2 if it's not a program value or an empty string
          productID:
            productID2 &&
            typeof productID2 === 'string' &&
            productID2 !== '' &&
            !programCodes.includes(productID2)
              ? productID2
              : '',
          planLengths: planLengths2 || [],
          planVersion: planVersion2,
        }),
      );
    } else {
      fetchPromises.push([]); // Empty placeholder for second set
    }

    Promise.all(fetchPromises).then(([plans1, plans2]) => {
      // Combine both arrays
      const allPlans = [...(plans1 || []), ...(plans2 || [])];
      // Group by productID
      const plansByCode = allPlans.reduce<{ [key: string]: ResolvedPlan[] }>(
        (acc, plan) => {
          if (!plan.code) return acc;
          const key = plan.code.split('-')[0]; // get part before first '-'
          if (!acc[key]) acc[key] = [];
          acc[key].push(plan);
          return acc;
        },
        {},
      );

      setPlansByCode(plansByCode);

      // Get all keys
      const keys = Object.keys(plansByCode);

      // Determine which key is suite and which is basic science
      const suiteKey = keys.find((key) => key.includes('suite'));
      const basicScienceKey = keys.find((key) => key.includes('bassci'));

      // Order the keys properly - suite first, basic science second, then any others
      let orderedKeys: string[] = [];
      if (suiteKey) orderedKeys.push(suiteKey);
      if (basicScienceKey) orderedKeys.push(basicScienceKey);

      // Add any other keys that aren't suite or basic science
      keys.forEach((key) => {
        if (key !== suiteKey && key !== basicScienceKey) {
          orderedKeys.push(key);
        }
      });

      // If we have no keys after filtering, fall back to the original keys
      if (orderedKeys.length === 0) {
        orderedKeys = keys;
      }

      // Use the first ordered key as the default plan
      const defaultPlan = orderedKeys[0];
      const secondButton = orderedKeys[1];

      // Set the plans using the first ordered key
      setPlans(plansByCode[defaultPlan] || []);

      // Set the keys in the correct order (suite first, basic science second)
      setKeys([defaultPlan, secondButton].filter(Boolean));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    program,
    planLengthsKey,
    productID,
    planVersion,
    program2,
    planLengths2Key,
    productID2,
    planVersion2,
    dealType,
    dealCode,
    dealCodesKey,
    fetch,
  ]);

  useEffect(() => {
    const isSuiteSelected = plans.every((plan) => plan.code.includes('suite'));
    if (isSuiteSelected) {
      setSuitePlans(null);
      return;
    }
    const suiteCode = keys.find((key) => key.includes('suite'));
    if (suiteCode) {
      setSuitePlans(plansByCode[suiteCode] || null);
    }
  }, [keys, plans, plansByCode]);

  return { plans, plansByCode, keys, suitePlans, setPlans };
}
